import { TRANSPORT_ADDRESS_MAX, TRANSPORT_NAME_MAX, type TransportPlace, makeCreateTransportPlaceRequest, makePatchTransportPlaceRequest } from "@seagames/contracts";
import { Field, IconButton, Icons, Modal, ModalActions, Select, type SelectOption, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { createPlace, deletePlace, invalidateFleet, placeHotelOptionsOptions, placesListOptions, updatePlace } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { conflictField } from "../common/apply-api-error";
import { SheetSubhead, inputClass, routeSaveError, useProblemText } from "./form-bits";
import { ClusterSelect } from "./hooks";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type Problems, hasProblems, issuesToProblems } from "./model";
import { InactiveChip } from "./parts";

type PlaceField = "name" | "address" | "cluster_id" | "is_active" | "lodging_building_id";
const FIELDS: readonly PlaceField[] = ["name", "address", "cluster_id", "is_active", "lodging_building_id"];

interface PlaceDraft {
	name: string;
	address: string;
	cluster_id: string;
	is_active: boolean;
	/** "" = not a hotel. */
	lodging_building_id: string;
}
const EMPTY: PlaceDraft = { name: "", address: "", cluster_id: "", is_active: true, lodging_building_id: "" };

/**
 * Transport places (PLAN X3): hotels, embassies, training grounds — every one
 * with a cluster, so drive time and the dispatch cluster are always known.
 * Venues and entry points are places already (their own settings pages give
 * them a cluster). A hotel place is LINKED to its lodging building (0034):
 * travellers with a room there are driven to / from it; one place per
 * building (409 HOTEL_ALREADY_LINKED), so a building another place holds is
 * offered disabled. Her cluster-sheet layout: the list, then the add / edit
 * form under it. A place a trip uses cannot be deleted (409); switch it off
 * instead.
 */
export function PlacesModal({ onClose }: { onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const problemText = useProblemText();
	const messages = useFleetFormMessages();
	const places = useQuery(placesListOptions());
	const hotels = useQuery(placeHotelOptionsOptions());
	const [editing, setEditing] = useState<TransportPlace | null>(null);
	const [draft, setDraft] = useState<PlaceDraft>(EMPTY);
	const [problems, setProblems] = useState<Problems<PlaceField>>({});
	const [busy, setBusy] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
	const nameRef = useRef<HTMLInputElement>(null);
	const items = places.data?.items ?? [];
	const hotelOptions = useMemo<SelectOption[]>(
		() =>
			(hotels.data ?? []).map((h) => {
				const takenBy = h.place && h.place.transport_place_id !== editing?.transport_place_id ? h.place.name : null;
				return { value: h.lodging_building_id, label: h.name, disabled: takenBy !== null, description: takenBy ? t("places.hotelTakenBy", { place: takenBy }) : undefined };
			}),
		[hotels.data, editing, t],
	);
	const pickHotel = (id: string) => {
		set("lodging_building_id", id);
		// A new hotel place starts in its building's cluster (the drive times M11 already uses).
		const cluster = hotels.data?.find((h) => h.lodging_building_id === id)?.cluster_id;
		if (cluster && !draft.cluster_id) set("cluster_id", cluster);
	};

	const reset = () => {
		setEditing(null);
		setDraft(EMPTY);
		setProblems({});
	};
	const set = <K extends PlaceField>(k: K, v: PlaceDraft[K]) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => ({ ...p, [k]: undefined }));
	};

	const save = async (e: React.FormEvent) => {
		e.preventDefault();
		const found: Problems<PlaceField> = {};
		if (!draft.name.trim()) found.name = { key: "placeNameRequired" };
		if (!draft.cluster_id) found.cluster_id = { key: "clusterRequired" };
		setProblems(found);
		if (hasProblems(found)) return;
		setBusy(true);
		try {
			if (editing) {
				const body = {
					name: draft.name !== editing.name ? draft.name : undefined,
					address: draft.address.trim() !== (editing.address ?? "") ? draft.address : undefined,
					cluster_id: draft.cluster_id !== editing.cluster.cluster_id ? draft.cluster_id : undefined,
					is_active: draft.is_active !== editing.is_active ? draft.is_active : undefined,
					lodging_building_id:
						draft.lodging_building_id !== (editing.lodging_building?.lodging_building_id ?? "") ? draft.lodging_building_id || null : undefined,
				};
				if (Object.values(body).every((v) => v === undefined)) {
					reset();
					return;
				}
				const parsed = makePatchTransportPlaceRequest(messages).safeParse({ ...body, reason: `Changed transport place ${editing.name}` });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, FIELDS, "name"));
					return;
				}
				await updatePlace(editing.transport_place_id, parsed.data);
				toast.success(t("places.toast.updated", { name: draft.name.trim() }));
			} else {
				const parsed = makeCreateTransportPlaceRequest(messages).safeParse({ ...draft, lodging_building_id: draft.lodging_building_id || null });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, FIELDS, "name"));
					return;
				}
				await createPlace(parsed.data);
				toast.success(t("places.toast.added", { name: parsed.data.name }));
			}
			await invalidateFleet();
			reset();
		} catch (err) {
			routeSaveError(err, FIELDS, setProblems, editing ? t("places.toast.updateFailed") : t("places.toast.addFailed"), (x) => {
				if (x.code === "HOTEL_ALREADY_LINKED") {
					return { lodging_building_id: typeof x.details?.["place_name"] === "string" ? { key: "hotelTaken", text: errorMessage(x) } : { key: "hotelTaken" } };
				}
				return conflictField(x) === "name" ? { name: { key: "placeNameTaken" } } : null;
			});
		} finally {
			setBusy(false);
		}
	};

	const remove = async (p: TransportPlace) => {
		setBusy(true);
		try {
			await deletePlace(p.transport_place_id);
			await invalidateFleet();
			toast.success(t("places.toast.deleted", { name: p.name }));
			if (editing?.transport_place_id === p.transport_place_id) reset();
		} catch (err) {
			toast.error(err instanceof ApiError && err.code === "CONFLICT" ? new Error(t("places.inUse")) : err, t("places.toast.deleteFailed"));
		} finally {
			setBusy(false);
			setConfirmDelete(null);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="lg"
			title={t("places.title")}
			description={t("places.description")}
			dismissible={!busy}
			footer={
				<ModalActions>
					{editing ? (
						<button type="button" className="btn btn-line btn-sm" onClick={reset} disabled={busy}>
							{t("places.cancelEdit")}
						</button>
					) : (
						<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
							{tc("action.close")}
						</button>
					)}
					<button type="submit" form="pl-form" className="btn btn-primary btn-sm" disabled={busy || places.isPending}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{editing ? t("places.save") : t("places.add")}
					</button>
				</ModalActions>
			}
		>
			<form id="pl-form" onSubmit={(e) => void save(e)} noValidate className="grid gap-[1rem]">
				{places.isPending ? (
					<div className="skeleton h-[8rem] rounded-[0.625rem]" />
				) : places.isError ? (
					<div role="alert" className="flex items-center justify-between gap-[1rem] rounded-field bg-error/8 px-[0.875rem] py-[0.75rem] text-sm">
						<span>{errorMessage(places.error)}</span>
						<button type="button" className="btn btn-xs btn-outline" onClick={() => void places.refetch()}>
							{tc("action.retry")}
						</button>
					</div>
				) : items.length === 0 ? (
					<p className="rounded-field bg-gms-soft px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-2">{t("places.none")}</p>
				) : (
					<ul className="divide-y divide-gms-line rounded-field border border-gms-line">
						{items.map((p) => (
							<li key={p.transport_place_id} className={`flex items-start gap-[0.75rem] px-[0.875rem] py-[0.625rem] ${editing?.transport_place_id === p.transport_place_id ? "bg-gms-navy-soft" : ""}`}>
								<span
									title={p.cluster.name}
									className="mt-[0.0625rem] inline-flex h-[1.375rem] min-w-[2.75rem] shrink-0 items-center justify-center rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] text-[0.6875rem] font-bold tracking-[0.06em] text-gms-navy"
								>
									{p.cluster.code}
								</span>
								<span className="grid min-w-0 flex-1 gap-[0.125rem]">
									<span className="flex flex-wrap items-center gap-[0.5rem]">
										<b className="text-[0.875rem] font-semibold text-gms-navy-ink">{p.name}</b>
										{!p.is_active && <InactiveChip />}
									</span>
									{p.address && <span className="text-[0.78125rem] text-gms-ink-2">{p.address}</span>}
									{p.lodging_building && (
										<span className="text-[0.78125rem] font-medium text-gms-navy">{t("places.hotelLinked", { name: p.lodging_building.name })}</span>
									)}
									<span className="text-[0.75rem] text-gms-ink-3">
										{p.trip_count > 0 ? t("places.trips", { count: p.trip_count, n: formatNumber(p.trip_count) }) : t("places.noTrips")}
									</span>
								</span>
								{confirmDelete === p.transport_place_id ? (
									<span className="flex shrink-0 flex-wrap items-center justify-end gap-[0.375rem]">
										<span className="text-[0.78125rem] text-gms-ink-2">{t("places.confirmDelete")}</span>
										<button type="button" className="btn btn-error btn-xs" disabled={busy} onClick={() => void remove(p)}>
											{t("places.delete")}
										</button>
										<button type="button" className="btn btn-line btn-xs" disabled={busy} onClick={() => setConfirmDelete(null)}>
											{t("places.keep")}
										</button>
									</span>
								) : (
									<span className="flex shrink-0 items-center gap-[0.125rem]">
										<IconButton
											label={t("places.editLabel", { name: p.name })}
											icon={<Icons.Pencil />}
											size="xs"
											variant="row"
											onClick={() => {
												setEditing(p);
												setDraft({
													name: p.name,
													address: p.address ?? "",
													cluster_id: p.cluster.cluster_id,
													is_active: p.is_active,
													lodging_building_id: p.lodging_building?.lodging_building_id ?? "",
												});
												setProblems({});
												nameRef.current?.focus();
											}}
										/>
										<IconButton
											label={p.trip_count > 0 ? t("places.deleteInUse", { name: p.name }) : t("places.deleteLabel", { name: p.name })}
											icon={<Icons.Trash />}
											size="xs"
											variant="rowDanger"
											disabled={p.trip_count > 0}
											onClick={() => setConfirmDelete(p.transport_place_id)}
										/>
									</span>
								)}
							</li>
						))}
					</ul>
				)}

				<div className="grid gap-[0.75rem] rounded-field border border-gms-line bg-gms-soft px-[0.875rem] py-[0.75rem] sm:grid-cols-2">
					<div className="sm:col-span-2">
						<SheetSubhead>{editing ? t("places.editTitle", { name: editing.name }) : t("places.addTitle")}</SheetSubhead>
					</div>
					<Field label={t("places.name")} htmlFor="pl-name" required error={problemText(problems.name)}>
						<input ref={nameRef} id="pl-name" value={draft.name} onChange={(e) => set("name", e.target.value)} maxLength={TRANSPORT_NAME_MAX} autoComplete="off" placeholder={t("places.namePlaceholder")} className={inputClass(!!problems.name)} />
					</Field>
					<Field label={t("places.cluster")} htmlFor="pl-cluster" required error={problemText(problems.cluster_id)}>
						<ClusterSelect id="pl-cluster" value={draft.cluster_id} onChange={(v) => set("cluster_id", v)} current={editing?.cluster} />
					</Field>
					<Field label={t("places.address")} htmlFor="pl-address" aside={tc("state.optional")} error={problemText(problems.address)} className="sm:col-span-2">
						<input id="pl-address" value={draft.address} onChange={(e) => set("address", e.target.value)} maxLength={TRANSPORT_ADDRESS_MAX} autoComplete="off" className={inputClass(!!problems.address)} />
					</Field>
					<Field
						label={t("places.hotel")}
						htmlFor="pl-hotel"
						aside={tc("state.optional")}
						hint={t("places.hotelHint")}
						error={problemText(problems.lodging_building_id)}
						className="sm:col-span-2"
					>
						<Select
							id="pl-hotel"
							value={draft.lodging_building_id || null}
							onChange={(v) => pickHotel(v ?? "")}
							options={hotelOptions}
							placeholder={hotels.isPending ? t("common.loading") : t("places.hotelNone")}
							clearable
						/>
					</Field>
					<label className="flex cursor-pointer items-start gap-[0.625rem] text-[0.84375rem] text-gms-navy-ink sm:col-span-2">
						<input type="checkbox" className="toggle toggle-sm toggle-primary mt-[0.0625rem]" checked={draft.is_active} onChange={(e) => set("is_active", e.target.checked)} />
						<span>
							<span className="font-medium">{t("places.active")}</span>
							<span className="block text-[0.75rem] text-gms-ink-3">{t("places.activeHint")}</span>
						</span>
					</label>
				</div>
			</form>
		</Modal>
	);
}
